import React, { useRef, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { FileCopyOutlined } from "@material-ui/icons";
import { Dialog, DialogContent, DialogTitle, Tooltip } from "@material-ui/core";

import { i18n } from "../../translate/i18n";

import DateUtils from "../../utils/classes/DateUtils";

const useStyles = makeStyles(theme => ({
  root: {
    userSelect: "none"
  },

  dialogContent: {
    display: "flex",
    flexDirection: "column",
    alignItems: "start",
    justifyContent: "center",
    gap: "1em",
  },

  infoContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "start",
    justifyContent: "center",
    gap: "1em",
    width: "100%",
  },

  copyButtonContainer: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "end",
    justifyContent: "center",
    padding: "1em",
  },

  copyButton: {
    cursor: "pointer",
    transition: "all 0.3s ease",
    "&:hover": { transform: "translateY(-5px)", }
  },

  hr: {
    width: "100%",
  },
}));

const QueueOverrideDashboardModal = ({ open, onClose, queueOverride }) => {
  //  ***************
  //  ** Variables **
  //  ***************
  const classes = useStyles();

  const timeoutCopyRef = useRef(null);
  
  const [copyTooltip, setCopyTooltip] = useState(i18n.t("queueOverrideDashboardModal.tooltips.copy"));



  //  ***************
  //  ** Functions **
  //  ***************
  const handleClose = () => {
    onClose();
  };

  const handleCopyInfo = async () => {
    const clipboardInfo = `${i18n.t("queueOverrideDashboardModal.title.up")}\n---\n${i18n.t("queueOverrideDashboardModal.spans.id")} ${queueOverride.id}\n${i18n.t("queueOverrideDashboardModal.spans.status")} ${i18n.t(`queueOverrideDashboardModal.spans.statusTypes.${queueOverride.status}`)}\n${i18n.t("queueOverrideDashboardModal.spans.requestedByUserName")} ${queueOverride.requestedByUserName}\n${i18n.t("queueOverrideDashboardModal.spans.resolvedByUserName")} ${queueOverride.resolvedByUserName}\n${i18n.t("queueOverrideDashboardModal.spans.ticketId")} ${queueOverride.ticketId}\n${i18n.t("queueOverrideDashboardModal.spans.contactName")} ${queueOverride.contactName}\n${i18n.t("queueOverrideDashboardModal.spans.queuePositionAtRequest")} ${queueOverride.queuePositionAtRequest}\n${i18n.t("queueOverrideDashboardModal.spans.queuePositionAtResolution")} ${queueOverride.queuePositionAtResolution}\n${i18n.t("queueOverrideDashboardModal.spans.ticketsWithRequestUserAtRequest")} ${queueOverride.ticketsWithRequestUserAtRequest}\n${i18n.t("queueOverrideDashboardModal.spans.ticketsWithRequestUserAtResolution")} ${queueOverride.ticketsWithRequestUserAtResolution}\n${i18n.t("queueOverrideDashboardModal.spans.createdAt")} ${formatCreatedAt(queueOverride.createdAt)}\n${i18n.t("queueOverrideDashboardModal.spans.requestReason")} ${queueOverride.requestReason}`;
    await navigator.clipboard.writeText(clipboardInfo);

    setCopyTooltip(i18n.t("queueOverrideDashboardModal.tooltips.copied"));

    if (timeoutCopyRef.current) clearTimeout(timeoutCopyRef.current);
    timeoutCopyRef.current = setTimeout(() => setCopyTooltip(i18n.t("queueOverrideDashboardModal.tooltips.copy")), 700);
  };

  const formatCreatedAt = (createdAt) => {
    return DateUtils.toLocaleDateString(createdAt);
  };



  //  ************
  //  ** Return **
  //  ************
  if (!open) return (<></>);

  return (
    <Dialog className={classes.root} open={open} onClose={handleClose} scroll="paper">
      <DialogTitle>
        {i18n.t("queueOverrideDashboardModal.title.up")}
      </DialogTitle>

      <DialogContent className={classes.dialogContent} dividers>
        <div className={classes.infoContainer}>
          <span>{i18n.t("queueOverrideDashboardModal.spans.id")} <b>{queueOverride.id}</b></span>
          <span>{i18n.t("queueOverrideDashboardModal.spans.status")} <b>{i18n.t(`queueOverrideDashboardModal.spans.statusTypes.${queueOverride.status}`)}</b></span>

          <hr className={classes.hr} />

          <span>{i18n.t("queueOverrideDashboardModal.spans.requestedByUserName")} <b>{queueOverride.requestedByUserName}</b></span>
          <span>{i18n.t("queueOverrideDashboardModal.spans.resolvedByUserName")} <b>{queueOverride.resolvedByUserName}</b></span>
          <span>{i18n.t("queueOverrideDashboardModal.spans.ticketId")} <b>{queueOverride.ticketId}</b></span>
          <span>{i18n.t("queueOverrideDashboardModal.spans.contactName")} <b>{queueOverride.contactName}</b></span>

          <hr className={classes.hr} />

          <span>{i18n.t("queueOverrideDashboardModal.spans.queuePositionAtRequest")} <b>{queueOverride.queuePositionAtRequest}</b></span>
          <span>{i18n.t("queueOverrideDashboardModal.spans.queuePositionAtResolution")} <b>{queueOverride.queuePositionAtResolution}</b></span>
          <span>{i18n.t("queueOverrideDashboardModal.spans.ticketsWithRequestUserAtRequest")} <b>{queueOverride.ticketsWithRequestUserAtRequest}</b></span>
          <span>{i18n.t("queueOverrideDashboardModal.spans.ticketsWithRequestUserAtResolution")} <b>{queueOverride.ticketsWithRequestUserAtResolution}</b></span>

          <hr className={classes.hr} />

          <span>{i18n.t("queueOverrideDashboardModal.spans.createdAt")} <b>{formatCreatedAt(queueOverride.createdAt)}</b></span>
          <span>{i18n.t("queueOverrideDashboardModal.spans.requestReason")} <b>{queueOverride.requestReason}</b></span>
        </div>
      </DialogContent>

      <div className={classes.copyButtonContainer}>
        <Tooltip title={copyTooltip} placement="top-start" arrow>
          <span className={classes.copyButton} onClick={async () => await handleCopyInfo()}>
            <FileCopyOutlined />
          </span>
        </Tooltip>
      </div>
    </Dialog>
  );
};

export default QueueOverrideDashboardModal;
